/**
 * After Supabase session exists, verifies account deletion job is not blocking before entering /app/.
 */

import { useEffect, useRef, useState } from 'react'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import {
  deletionJobStatusBlocksApp,
  fetchAccountDeletionJobStatusWithRetries,
  signInBannerForBlockedDeletionJobStatus,
} from '../helpers/accountDeletionStatusApi.helper'
import { setPendingSignInBannerMessage } from '../helpers/signInBannerTransfer.helper'
import { logger } from '../../helpers/logger.helper'
import { authLiveDebug } from '../helpers/authLiveDebug.helper'

export type AccountDeletionAppEntryPhase = 'inactive' | 'verifying' | 'allow_redirect'

export function useAccountDeletionGateBeforeAppEntry(): AccountDeletionAppEntryPhase {
  const { isLoaded, isSignedIn, isAnonymous, getToken, signOut } = useSupabaseAuth()
  const [phase, setPhase] = useState<AccountDeletionAppEntryPhase>('inactive')
  const phaseStartRef = useRef<number>(0)

  useEffect(() => {
    authLiveDebug('DeletionGate inputs', { isLoaded, isSignedIn, isAnonymous, phase })
  }, [isLoaded, isSignedIn, isAnonymous, phase])

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setPhase('inactive')
      return
    }

    // Anonymous users cannot have scheduled deletion jobs — skip the gate entirely.
    if (isAnonymous) {
      authLiveDebug('DeletionGate skip — anonymous user')
      setPhase('allow_redirect')
      return
    }

    let cancelled = false
    setPhase('verifying')
    phaseStartRef.current = Date.now()
    authLiveDebug('DeletionGate verifying START')
    logger.info('[DeletionGate] starting verification', { ts: phaseStartRef.current })

    void (async () => {
      const tokenStart = Date.now()
      const token = await getToken({ skipCache: true })
      authLiveDebug('DeletionGate getToken done', {
        ok: !!token,
        ms: Date.now() - tokenStart,
      })
      logger.info('[DeletionGate] getToken done', {
        ok: !!token, ms: Date.now() - tokenStart,
      })
      if (!token) {
        authLiveDebug('DeletionGate no token — phase inactive')
        logger.warn('[DeletionGate] no token — setting phase inactive')
        if (!cancelled) setPhase('inactive')
        return
      }

      const apiStart = Date.now()
      const result = await fetchAccountDeletionJobStatusWithRetries(token)
      authLiveDebug('DeletionGate API done', {
        ok: result.ok,
        jobStatus: result.ok ? result.jobStatus : undefined,
        ms: Date.now() - apiStart,
        totalMs: Date.now() - phaseStartRef.current,
      })
      logger.info('[DeletionGate] api call done', {
        ok: result.ok, jobStatus: result.ok ? result.jobStatus : undefined,
        ms: Date.now() - apiStart, totalMs: Date.now() - phaseStartRef.current,
      })
      if (cancelled) return

      if (
        result.ok &&
        result.jobStatus !== null &&
        deletionJobStatusBlocksApp(result.jobStatus, result.lastErrorCode)
      ) {
        authLiveDebug('DeletionGate BLOCKED by deletion job', {
          jobStatus: result.jobStatus,
          lastErrorCode: result.lastErrorCode,
        })
        logger.warn('[DeletionGate] account blocked by deletion job', {
          jobStatus: result.jobStatus, lastErrorCode: result.lastErrorCode,
        })
        try {
          await signOut()
        } catch {
          // signOut best-effort; still show banner
        }
        if (!cancelled) {
          setPendingSignInBannerMessage(
            signInBannerForBlockedDeletionJobStatus(result.jobStatus, result.lastErrorCode)
          )
          setPhase('inactive')
        }
        return
      }

      authLiveDebug('DeletionGate allow_redirect', {
        totalMs: Date.now() - phaseStartRef.current,
      })
      logger.info('[DeletionGate] allow_redirect', {
        totalMs: Date.now() - phaseStartRef.current,
      })
      if (!cancelled) setPhase('allow_redirect')
    })()

    return () => {
      cancelled = true
    }
  }, [isLoaded, isSignedIn, isAnonymous, getToken, signOut])

  return phase
}
